import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Flame, ChevronRight, ArrowLeft, BookOpen, Zap, Library, Crown, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { TaskSection } from '../components/app/TaskSection';
import { MentorAIPanel } from '../components/app/MentorAIPanel';
import { SpecTrainingSection } from '../components/app/SpecTrainingSection';
import { FreeChatPanel } from '../components/app/FreeChatPanel';
import PromptOriginLifeSection from '../components/app/PromptOriginLifeSection';
import { getTasksForDay } from '../data/dayTasks';

export const TodayPage = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMentor, setShowMentor] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [practiceDay, setPracticeDay] = useState(null);

  useEffect(() => {
    refreshUser();
    const fetchLessons = async () => {
      try {
        const res = await api.get('/lessons');
        setLessons(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchLessons();
    
    const saved = localStorage.getItem('completedTasks');
    if (saved) setCompletedTasks(JSON.parse(saved));
  }, [api]);

  const completedDays = user?.completed_days || [];
  const isPremium = user?.subscription_status === 'active';
  const nextDay = completedDays.length > 0 ? Math.max(...completedDays) + 1 : 1;
  const currentLesson = lessons.find(l => l.day_number === nextDay) || lessons[0];
  const progressPercent = lessons.length > 0 ? (completedDays.length / lessons.length) * 100 : 0;

  // Available days for practice: all completed + current (next to complete)
  const availableDays = (() => {
    const days = [];
    const sorted = [...completedDays].sort((a, b) => a - b);
    sorted.forEach(d => days.push({ number: d, isCurrent: false }));
    const currentDayNum = currentLesson?.day_number || 1;
    if (!sorted.includes(currentDayNum)) {
      days.push({ number: currentDayNum, isCurrent: true });
    }
    return days;
  })();

  // Selected practice day — default to current
  const activePracticeDay = practiceDay || (currentLesson?.day_number || 1);
  const practiceDayTasks = getTasksForDay(activePracticeDay);

  const handleStartTask = (task) => {
    setSelectedTask(task);
    setShowMentor(true);
  };

  const handleOpenMentor = () => {
    setSelectedTask(null);
    setShowMentor(true);
  };

  const handleCloseMentor = () => {
    setShowMentor(false);
    if (selectedTask && !completedTasks.includes(selectedTask.id)) {
      const newCompleted = [...completedTasks, selectedTask.id];
      setCompletedTasks(newCompleted);
      localStorage.setItem('completedTasks', JSON.stringify(newCompleted));
    }
    setSelectedTask(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pb-20 px-6">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
        <p className="mt-6 text-center text-sm text-slate-400 max-w-xs leading-relaxed">
          Przygotowujemy PromptOrigin dla Ciebie. Ze względu na złożoność platformy ładowanie może potrwać chwilę.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ background: 'transparent' }} data-testid="today-page">
      {/* Header with gradient overlay */}
      <div className="relative px-6 pt-8 pb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-500/20 to-emerald-500/10" />
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-3">
            <button 
              onClick={() => navigate('/profile')} 
              className="text-white/70 hover:text-white transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            {/* Premium shortcut */}
            <button
              onClick={() => navigate(isPremium ? '/profile' : '/paywall')}
              className="relative group"
              data-testid="premium-shortcut-btn"
              aria-label={isPremium ? 'Twój plan Premium' : 'Plany Premium'}
            >
              <div 
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 ${
                  isPremium 
                    ? 'bg-amber-500/15 border border-amber-400/25 group-hover:bg-amber-500/25 group-hover:border-amber-400/40' 
                    : 'bg-white/[0.06] border border-white/[0.08] group-hover:bg-white/[0.10] group-hover:border-white/[0.14]'
                }`}
                style={isPremium ? { boxShadow: '0 2px 12px rgba(251,191,36,0.12)' } : {}}
              >
                <Crown className={`w-4 h-4 transition-colors duration-300 ${
                  isPremium 
                    ? 'text-amber-400 group-hover:text-amber-300' 
                    : 'text-slate-400 group-hover:text-white'
                }`} />
              </div>
              {isPremium && (
                <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 border border-[#0c1222]" />
              )}
            </button>
          </div>
          
          <p className="text-sky-300/90 text-sm mb-0.5">{user?.email?.split('@')[0]}</p>
          <h1 className="text-xl font-bold text-white mb-4">Twój plan na dziś</h1>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5">
              <Flame className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-semibold text-white">{user?.current_streak || 0}</span>
            </div>
            <span className="text-xs text-slate-400">
              Najdłuższa seria: {user?.longest_streak || 0} dni
            </span>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-2 space-y-5">
        {/* Progress Card - Compact */}
        <Card data-card="progress" className="border-0 rounded-2xl" style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)' }}>
          <CardContent className="py-4 px-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-slate-400">Twój postęp</span>
              <span className="text-xs font-bold text-sky-400">{Math.round(progressPercent)}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" style={{ background: 'rgba(255,255,255,0.08)' }} />
            <p className="text-xs text-slate-500 mt-1.5">
              {completedDays.length} z {lessons.length} lekcji ukończonych
            </p>
          </CardContent>
        </Card>

        {/* Current Lesson Card - Main CTA */}
        {currentLesson && (
          <Card 
            data-card="lesson" 
            className="border-0 overflow-hidden rounded-2xl"
            style={{ 
              background: 'linear-gradient(135deg, rgba(14,165,233,0.15) 0%, rgba(16,185,129,0.1) 100%)', 
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="w-4 h-4 text-sky-400" />
                <CardDescription className="text-sky-400 text-xs font-medium">Dzień {currentLesson.day_number}</CardDescription>
              </div>
              <CardTitle className="text-lg text-white">{currentLesson.title}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <Button
                onClick={() => navigate(`/lesson/${currentLesson.day_number}`)}
                className="w-full h-11 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-lg shadow-sky-500/20 transition-all duration-200"
                data-testid="open-lesson-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                Otwórz lekcję
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <div className="grid grid-cols-2 gap-2.5 mt-3">
                <div className="rounded-xl px-3 py-2.5 flex items-center gap-2.5" style={{ background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.1)' }}>
                  <p className="text-lg font-bold text-sky-400 leading-none">{completedDays.length}</p>
                  <p className="text-[11px] text-slate-400 leading-tight">Ukończone<br/>lekcje</p>
                </div>
                <div className="rounded-xl px-3 py-2.5 flex items-center gap-2.5" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.1)' }}>
                  <p className="text-lg font-bold text-emerald-400 leading-none">{user?.current_streak || 0}</p>
                  <p className="text-[11px] text-slate-400 leading-tight">Aktywna<br/>seria</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tasks Section */}
        {practiceDayTasks && (
          <TaskSection
            dayNumber={activePracticeDay}
            dayTitle={practiceDayTasks.dayTitle}
            dayContext={practiceDayTasks.dayContext}
            tasks={practiceDayTasks.tasks}
            completedTasks={completedTasks}
            onStartTask={handleStartTask}
            onOpenMentor={handleOpenMentor}
            availableDays={availableDays}
            selectedDay={activePracticeDay}
            onDayChange={setPracticeDay}
          />
        )}

        {/* Prompt Library CTA */}
        <button
          onClick={() => navigate('/library')}
          className="w-full rounded-xl overflow-hidden transition-all group text-left"
          style={{
            background: 'linear-gradient(135deg, rgba(6,182,212,0.12) 0%, rgba(14,165,233,0.08) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(6,182,212,0.15)',
            boxShadow: '0 2px 20px rgba(6,182,212,0.06)',
          }}
          data-testid="open-prompt-library"
        >
          <div className="px-5 py-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)', boxShadow: '0 4px 16px rgba(6,182,212,0.3)' }}>
              <Library className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold text-white group-hover:text-cyan-300 transition-colors">
                Biblioteka promptów
              </p>
              <p className="text-xs text-slate-400 mt-0.5">Sprawdzone prompty do natychmiastowego użycia</p>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-cyan-400 transition-all group-hover:translate-x-1 flex-shrink-0" />
          </div>
        </button>

        {/* Free Chat — card */}
        <button
          onClick={() => setShowChat(true)}
          className="w-full rounded-xl overflow-hidden transition-all group text-left"
          style={{
            background: 'linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(6,182,212,0.06) 100%)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(16,185,129,0.12)',
            boxShadow: '0 2px 16px rgba(16,185,129,0.04)',
          }}
          data-testid="open-free-chat"
        >
          <div className="px-5 py-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ 
                background: 'linear-gradient(135deg, #10b981, #059669)',
                boxShadow: '0 4px 16px rgba(16,185,129,0.25)' 
              }}
            >
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <p className="flex-1 text-[15px] font-bold text-white group-hover:text-emerald-300 transition-colors leading-snug">
              Swobodna rozmowa ze sztuczną inteligencją
            </p>
            <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-emerald-400 transition-all group-hover:translate-x-0.5 flex-shrink-0" />
          </div>
        </button>

        {/* Spec Training Section */}
        <SpecTrainingSection
          user={user}
          onNavigatePaywall={() => navigate('/paywall')}
        />

        {/* PromptOrigin Life Section */}
        <PromptOriginLifeSection user={user} />
      </div>

      {/* Mentor AI Panel */}
      {showMentor && (
        <MentorAIPanel
          onClose={handleCloseMentor}
          dayNumber={activePracticeDay}
          dayTitle={practiceDayTasks?.dayTitle || ''}
          dayContext={practiceDayTasks?.dayContext || ''}
          task={selectedTask}
        />
      )}

      {/* Free Chat Panel */}
      {showChat && (
        <FreeChatPanel onClose={() => setShowChat(false)} />
      )}
    </div>
  );
};

export default TodayPage;
